// The setup phase of the "Autogenerate from gwbk" dialog: pick a gateway backup,
// choose capture options (samples per dynamic route, interaction mode, nav
// rounds), and confirm an example path for each dynamic route. Owns all
// setup-only state plus the begin mutation, and reports the started job id up
// via `onStarted`. Renders its own dialog footer (Cancel / Start).

import { useEffect, useMemo, useState } from 'react'
import { Loader2 } from 'lucide-react'
import {
  useBeginGwbkAutogenMutation,
  useGetGwbkAutogenPlanQuery,
  useListGwbkSourcesQuery,
  type GwbkInteractionMode,
  type GwbkSampleSource,
  type GwbkSource
} from '@axilon-apps/domains-ontology-ui-state'

import { Button } from '../../components/ui/button'
import { DialogFooter } from '../../components/ui/dialog'
import { Input } from '../../components/ui/input'
import { Label } from '../../components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '../../components/ui/select'

export interface GwbkAutogenSetupFormProps {
  onClose: () => void
  /** Called once a run has started, with its job id. */
  onStarted: (jobId: string) => void
}

function sourceKey(source: GwbkSource): string {
  return `${source.siteId}::${source.gatewayId}`
}

/** Short, human label for where a suggested example path came from. */
const SAMPLE_SOURCE_LABEL: Record<GwbkSampleSource, string> = {
  manual: 'your example',
  graph: 'from graph',
  'view-default': 'view default',
  placeholder: 'placeholder — edit me'
}

const GwbkAutogenSetupForm = ({ onClose, onStarted }: GwbkAutogenSetupFormProps) => {
  const sourcesQuery = useListGwbkSourcesQuery()
  const sources = useMemo(() => sourcesQuery.data ?? [], [sourcesQuery.data])

  const [selectedKey, setSelectedKey] = useState('')
  const [maxDynamicSamples, setMaxDynamicSamples] = useState(1)
  const [interactionMode, setInteractionMode] = useState<GwbkInteractionMode>('agent')
  const [maxExploreSteps, setMaxExploreSteps] = useState(4)
  // routeKey → example path the user will screenshot (pre-filled with a guess).
  const [overrides, setOverrides] = useState<Record<string, string>>({})
  const [beginError, setBeginError] = useState<string | null>(null)

  const [beginAutogen, beginState] = useBeginGwbkAutogenMutation()

  const selectedSource = useMemo(
    () => sources.find((s) => sourceKey(s) === selectedKey),
    [sources, selectedKey]
  )

  // Preview the routes (static count + dynamic routes with suggested examples)
  // once a backup is picked, so we can ask the user to confirm each example.
  const planQuery = useGetGwbkAutogenPlanQuery(
    { siteId: selectedSource?.siteId ?? '', gatewayId: selectedSource?.gatewayId ?? '' },
    { skip: !selectedSource }
  )
  const dynamicRoutes = useMemo(() => planQuery.data?.dynamicRoutes ?? [], [planQuery.data])

  // Group dynamic routes by the project they belong to for the UI.
  const dynamicRoutesByProject = useMemo(() => {
    const groups = new Map<string, typeof dynamicRoutes>()
    for (const route of dynamicRoutes) {
      const existing = groups.get(route.project)
      if (existing) existing.push(route)
      else groups.set(route.project, [route])
    }
    return Array.from(groups.entries()).sort(([a], [b]) => a.localeCompare(b))
  }, [dynamicRoutes])

  // Switching backups invalidates the typed examples.
  useEffect(() => {
    setOverrides({})
  }, [selectedKey])

  const handleStart = async () => {
    if (!selectedSource) return
    setBeginError(null)
    // Only send non-empty examples; cleared routes fall back to auto-sampling.
    const dynamicOverrides = Object.fromEntries(
      Object.entries(overrides).filter(([, value]) => value.trim() !== '')
    )
    try {
      const result = await beginAutogen({
        siteId: selectedSource.siteId,
        gatewayId: selectedSource.gatewayId,
        maxDynamicSamples,
        interactionMode,
        ...(interactionMode === 'agent' ? { maxExploreSteps } : {}),
        ...(Object.keys(dynamicOverrides).length > 0 ? { dynamicOverrides } : {})
      }).unwrap()
      onStarted(result.jobId)
    } catch (err) {
      const message =
        typeof err === 'object' && err !== null && 'data' in err
          ? ((err as { data?: { error?: string } }).data?.error ?? 'Failed to start capture.')
          : 'Failed to start capture.'
      setBeginError(message)
    }
  }

  return (
    <>
      <div className="flex min-h-0 flex-1 flex-col gap-4 overflow-auto py-1 pr-1">
        <p className="m-0 text-sm text-ink-3">
          Pick a gateway backup. We&apos;ll screenshot every Perspective page-config route on its
          non-inheritable projects, sampling dynamic routes. You can download the results when
          it&apos;s done.
        </p>

        <div className="flex flex-col gap-1.5">
          <Label htmlFor="gwbk-source">Gateway backup</Label>
          {sourcesQuery.isLoading ? (
            <div className="flex items-center gap-2 text-sm text-ink-3">
              <Loader2 className="size-4 animate-spin" aria-hidden />
              Loading backups…
            </div>
          ) : sources.length === 0 ? (
            <p className="m-0 text-sm text-ink-3">
              No gateway backups found. Ingest a .gwbk first (Ingest page).
            </p>
          ) : (
            <Select value={selectedKey} onValueChange={setSelectedKey}>
              <SelectTrigger id="gwbk-source" className="w-full">
                <SelectValue placeholder="Select a gateway backup…" />
              </SelectTrigger>
              <SelectContent>
                {sources.map((source) => (
                  <SelectItem key={sourceKey(source)} value={sourceKey(source)}>
                    {source.gatewayId} — {source.fileName}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
        </div>

        <div className="flex flex-wrap items-end gap-4">
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="gwbk-samples">Samples per dynamic route</Label>
            <Input
              id="gwbk-samples"
              type="number"
              min={1}
              max={5}
              value={maxDynamicSamples}
              onChange={(e) =>
                setMaxDynamicSamples(
                  Math.max(1, Math.min(5, Number.parseInt(e.target.value, 10) || 1))
                )
              }
              className="w-28"
            />
          </div>

          <div className="flex flex-col gap-1.5">
            <Label htmlFor="gwbk-interaction">Interaction</Label>
            <Select
              value={interactionMode}
              onValueChange={(next) => setInteractionMode(next as GwbkInteractionMode)}
            >
              <SelectTrigger id="gwbk-interaction" className="w-52">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="agent">AI explore (tabs, dropdowns…)</SelectItem>
                <SelectItem value="tabs">Tabs only (fast)</SelectItem>
                <SelectItem value="none">Base page only</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {interactionMode === 'agent' ? (
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="gwbk-steps">Max nav rounds / page</Label>
              <Input
                id="gwbk-steps"
                type="number"
                min={1}
                max={8}
                value={maxExploreSteps}
                onChange={(e) =>
                  setMaxExploreSteps(
                    Math.max(1, Math.min(8, Number.parseInt(e.target.value, 10) || 4))
                  )
                }
                className="w-28"
              />
            </div>
          ) : null}
        </div>

        {interactionMode === 'agent' ? (
          <p className="m-0 text-xs text-ink-3">
            AI explore looks at each page and, per round, selects every tab / section switcher to
            click and capture (re-checking for sub-navigation each round). One vision-model call per
            round, so it&apos;s slower and costs more than tab cycling.
          </p>
        ) : null}

        {selectedSource ? (
          planQuery.isFetching ? (
            <div className="flex items-center gap-2 text-sm text-ink-3">
              <Loader2 className="size-4 animate-spin" aria-hidden />
              Reading routes…
            </div>
          ) : dynamicRoutes.length > 0 ? (
            <div className="flex min-h-0 flex-col gap-2">
              <div className="flex flex-col gap-0.5">
                <Label>Dynamic routes — confirm an example for each</Label>
                <p className="m-0 text-xs text-ink-3">
                  These routes need a real value to render. We pre-filled a best guess; edit any to
                  a working path.
                  {typeof planQuery.data?.staticCount === 'number'
                    ? ` ${planQuery.data.staticCount} static route(s) are captured as-is.`
                    : ''}
                </p>
              </div>
              <div className="flex max-h-72 flex-col gap-3 overflow-auto rounded border border-line bg-bg-sunk/30 p-2.5">
                {dynamicRoutesByProject.map(([project, routes]) => (
                  <div key={project} className="flex flex-col gap-2">
                    <div className="flex items-center gap-2">
                      <span className="truncate text-xs font-semibold text-ink-2" title={project}>
                        {project}
                      </span>
                      <span className="text-10 text-ink-3">
                        {routes.length} route{routes.length === 1 ? '' : 's'}
                      </span>
                      <span className="h-px flex-1 bg-line" aria-hidden />
                    </div>
                    {routes.map((route) => (
                      <div
                        key={`${route.project}${route.routeKey}`}
                        className="flex flex-col gap-1 pl-2"
                      >
                        <div className="flex items-center justify-between gap-2">
                          <span
                            className="truncate font-mono text-xs text-ink-2"
                            title={route.routeKey}
                          >
                            {route.routeKey}
                          </span>
                          <span className="shrink-0 text-10 uppercase tracking-wide text-ink-3">
                            {SAMPLE_SOURCE_LABEL[route.suggestedSource]}
                          </span>
                        </div>
                        <Input
                          value={overrides[route.routeKey] ?? ''}
                          onChange={(e) =>
                            setOverrides((prev) => ({ ...prev, [route.routeKey]: e.target.value }))
                          }
                          placeholder={route.suggestedPath}
                          className="font-mono text-xs"
                        />
                      </div>
                    ))}
                  </div>
                ))}
              </div>
            </div>
          ) : planQuery.data ? (
            <p className="m-0 text-xs text-ink-3">
              No dynamic routes — {planQuery.data.staticCount} static route(s) will be captured.
            </p>
          ) : null
        ) : null}

        {beginError ? <p className="m-0 text-sm text-destructive">{beginError}</p> : null}
      </div>

      <DialogFooter>
        <Button variant="outline" onClick={onClose}>
          Cancel
        </Button>
        <Button
          onClick={() => void handleStart()}
          disabled={!selectedSource || beginState.isLoading}
        >
          {beginState.isLoading ? (
            <>
              <Loader2 className="size-4 animate-spin" aria-hidden />
              Starting…
            </>
          ) : (
            'Start'
          )}
        </Button>
      </DialogFooter>
    </>
  )
}

export default GwbkAutogenSetupForm
